/**
 * Comparing vendor order numbers that were typed by hand.
 *
 * Marvin prints its order number as three letters and five digits
 * ("DMC00247"); the office copies it onto the ack. Two of the ways that goes
 * wrong showed up on one truck (2026-09-16): a zero too many ("DMC000247")
 * and one character off ("DNC00248" for "DMC00248", "DPR00012" for
 * "DPR00011"). A number that is wrong by that little is almost certainly the
 * same order, so the packing-slip matcher and the import dialog treat it as a
 * near match to put in front of a person -- never as an exact one to act on.
 *
 * Pure, and a mirror of functions/vendorOrderNumber.js (the sync's copy);
 * keep the two in step -- the specs share their cases.
 */

/** Upper-cased, trimmed, with no internal whitespace. */
export const normalizeVendorOrder = (value) =>
  value == null ? "" : String(value).toUpperCase().replace(/\s+/g, "");

/**
 * The number with every run of leading zeros in its digit groups removed:
 * "DMC000247", "DMC00247" and "DMC247" all key to "DMC247". Numbers with no
 * digit groups key to themselves.
 */
export const vendorOrderKey = (value) =>
  normalizeVendorOrder(value).replace(/0*(\d+)/g, (_, digits) =>
    String(Number(digits))
  );

/**
 * Whether two strings are one edit apart: one character substituted,
 * inserted, deleted, or two adjacent characters swapped.
 */
export function withinOneEdit(a, b) {
  if (a === b) return true;
  const la = a.length;
  const lb = b.length;
  if (Math.abs(la - lb) > 1) return false;
  let i = 0;
  while (i < la && i < lb && a[i] === b[i]) i++;
  if (la === lb) {
    // Substitution, or an adjacent transposition.
    if (a.slice(i + 1) === b.slice(i + 1)) return true;
    return (
      a[i] === b[i + 1] &&
      a[i + 1] === b[i] &&
      a.slice(i + 2) === b.slice(i + 2)
    );
  }
  // Insertion or deletion at i.
  return la < lb
    ? a.slice(i) === b.slice(i + 1)
    : a.slice(i + 1) === b.slice(i);
}

const lettersOf = (s) => (s.match(/^[A-Z]+/) || [""])[0];

/**
 * How alike two vendor order numbers are.
 *
 *   "exact"     the same number once whitespace and case are ignored
 *   "zeros"     the same letters and digits, differing only in leading zeros
 *   "one-edit"  one character substituted, inserted, deleted or swapped
 *   null        different orders
 *
 * Blank input is never close to anything: an ack with no number must not be
 * offered as the match for every slip. A one-edit match must still share
 * its letters: "DMC00247" and "DPR00247" are two accounts' orders, not a
 * typo of each other.
 *
 * @returns {{ close: boolean, reason: string|null }}
 */
export function vendorOrderSimilarity(a, b) {
  const x = normalizeVendorOrder(a);
  const y = normalizeVendorOrder(b);
  if (!x || !y) return { close: false, reason: null };
  if (x === y) return { close: true, reason: "exact" };
  if (vendorOrderKey(x) === vendorOrderKey(y)) {
    return { close: true, reason: "zeros" };
  }
  if (
    lettersOf(x) &&
    x.length >= 5 &&
    y.length >= 5 &&
    withinOneEdit(x, y) &&
    withinOneEdit(lettersOf(x), lettersOf(y))
  ) {
    return { close: true, reason: "one-edit" };
  }
  return { close: false, reason: null };
}

/**
 * A sale's vendor quote numbers, `[{ vendor, number }]`, as they are stored:
 * trimmed, without blanks, and each number once per vendor (whitespace and
 * case ignored, the first spelling kept). One vendor can hold several: a
 * salesperson may enter one job into the vendor's system as several quotes.
 */
export function cleanVendorQuotes(quotes) {
  const seen = new Set();
  const clean = [];
  (Array.isArray(quotes) ? quotes : []).forEach((quote) => {
    const vendor = String((quote && quote.vendor) || "").trim();
    const number = String((quote && quote.number) || "").trim();
    const key = vendorQuoteKey(vendor, number);
    if (!vendor || !number || seen.has(key)) return;
    seen.add(key);
    clean.push({ vendor, number });
  });
  return clean;
}

/** One vendor's quote number, whitespace and case ignored. */
export const vendorQuoteKey = (vendor, number) =>
  `${vendor}|${normalizeVendorOrder(number)}`;

/**
 * The vendor sync's checks (Sale.vendorQuoteChecks) that are about a number
 * `vendorQuotes` still holds. One about a number since removed or corrected
 * describes nothing on the sale.
 */
export function vendorQuoteChecksOn(checks, vendorQuotes) {
  const recorded = new Set(
    (vendorQuotes || []).map((quote) =>
      vendorQuoteKey(quote.vendor, quote.number)
    )
  );
  return (checks || []).filter(
    (check) =>
      check &&
      recorded.has(vendorQuoteKey(check.vendor, check.vendorQuoteNumber))
  );
}

const RANK = { exact: 0, zeros: 1, "one-edit": 2 };

/**
 * The closest of `candidates` to `target`, or null when none is close.
 * Exact beats zeros beats one-edit; ties keep the first candidate.
 *
 * @param {string} target
 * @param {Array<{ vendorOrderNumber: string }>} candidates
 * @returns {{ candidate, reason }|null}
 */
export function closestVendorOrder(target, candidates) {
  let best = null;
  (candidates || []).forEach((candidate) => {
    const { close, reason } = vendorOrderSimilarity(
      target,
      candidate && candidate.vendorOrderNumber
    );
    if (!close) return;
    if (!best || RANK[reason] < RANK[best.reason]) {
      best = { candidate, reason };
    }
  });
  return best;
}

/**
 * Every candidate that is close to `target`, best first, each with why.
 *
 * @returns {Array<{ candidate, reason }>}
 */
export function nearVendorOrders(target, candidates) {
  return (candidates || [])
    .map((candidate) => ({
      candidate,
      ...vendorOrderSimilarity(
        target,
        candidate && candidate.vendorOrderNumber
      ),
    }))
    .filter((entry) => entry.close)
    .sort((a, b) => RANK[a.reason] - RANK[b.reason])
    .map(({ candidate, reason }) => ({ candidate, reason }));
}

/**
 * The sale a dealer PO names, read the way Marvin echoes it.
 *
 * A PO defaults to projectID + saleID -- eight digits and a letter -- and
 * users append their own after a separator ("04300086A CUSTOM-PO"); a field
 * service order wraps it ("S05400025B-FSO"). Marvin's copy may have lost the
 * leading zero ("6400017A" for 06400017A), which is restored: a project id is
 * always eight digits, so seven can only be that. Null when the PO is
 * something else entirely.
 *
 * @returns {{ projectID: string, saleID: string, core: string }|null}
 */
export function poCore(value) {
  const po = value == null ? "" : String(value).trim();
  const m = po.match(/^(S)?(\d{7,8})([A-Za-z])(?:[\s\-–—/]+(.*))?$/);
  if (!m) return null;
  const projectID = m[2].padStart(8, "0");
  const saleID = m[3].toUpperCase();
  return { projectID, saleID, core: projectID + saleID };
}

/**
 * Whether two POs name the same sale: both resolve to a sale and it is the
 * same one, or, failing that, they are the same text.
 */
export function poMatches(a, b) {
  const x = poCore(a);
  const y = poCore(b);
  if (x && y) return x.core === y.core;
  const norm = (v) => (v == null ? "" : String(v).trim().toUpperCase());
  return Boolean(norm(a)) && norm(a) === norm(b);
}

/**
 * Does the PO Marvin echoes on a shipment line name this sale?
 *
 *   true    it resolves to this project and sale
 *   false   it resolves to ANOTHER sale
 *   null    it cannot be read (a hand-typed PO, or none), so nothing can be
 *           said
 *
 * The client's copy of vendorPOMatches in functions/vendorShippingParse.js
 * (poCore is its parseVendorPO); the specs share their cases.
 *
 * @returns {boolean|null}
 */
export function vendorPOMatches(customerPONumber, projectID, saleID) {
  const parsed = poCore(customerPONumber);
  if (!parsed) return null;
  return (
    parsed.projectID === String(projectID) &&
    parsed.saleID === String(saleID || "").toUpperCase()
  );
}

/**
 * Marvin's PO for a shipment line when it does not name the order the line
 * is stored under -- it names another sale, or none at all and is not the
 * order's own PO as typed -- else "". A blank PO says nothing, so it is "".
 *
 * The sync stores an order whose PO it cannot read under the ack that names
 * the order number (functions/vendorShippingSync.js checkVendorPO). On
 * 2026-09-30 two such orders were another job's -- "JC-DAYTON" and
 * "JCSNA-2026" -- and the dock received their packages under our POs.
 *
 * `verifiedPOs` are the unreadable POs on an order the sync has shown to be
 * the sale's -- Marvin's PO naming it on another line, the order's own PO,
 * the sale's quote number, the office's "This order is ours" -- stored on
 * the shipment as `verifiedVendorPOs` (verifiedPOsOf): they are not asked
 * about either. They never excuse a PO naming another sale.
 *
 * @param {string} customerPONumber
 * @param {{ projectID: string, saleID: string, orderPO?: string,
 *   verifiedPOs?: string[] }} order
 * @returns {string}
 */
export function vendorPOElsewhere(customerPONumber, order) {
  const po = customerPONumber == null ? "" : String(customerPONumber).trim();
  if (!po) return "";
  const matches = vendorPOMatches(po, order.projectID, order.saleID);
  if (matches) return "";
  const text = (value) =>
    String(value || "")
      .trim()
      .toUpperCase();
  const upper = po.toUpperCase();
  if (upper === text(order.orderPO)) return "";
  if (
    matches === null &&
    (Array.isArray(order.verifiedPOs) ? order.verifiedPOs : []).some(
      (verified) => upper === text(verified)
    )
  ) {
    return "";
  }
  return po;
}
